import _ from 'lodash';
import React, {useState, useEffect} from 'react';
import REST from 'go/requests/rest';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {
  Segment,
  Header,
  Icon,
  Loader,
  Input,
  Button,
  Pagination,
} from 'semantic-ui-react';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {
  compileContent,
  showDollarAmountWithDollar,
  isSmaOrSmu,
  formatLineNumber,
  formatDateMMDDYYYY,
} from 'common/utils';
import FundingOpportunityCommitmentModal from 'go/components/fo/FundingOpportunityCommitmentModal';
import FundingOpportunityContext from 'go/components/fo/FundingOpportunityContext';
import AddFOCContainerModal from 'go/components/fo/AddFOCContainerModal';
import FOCommitmentSearchModal from 'go/components/fo/FOCommitmentSearchModal';

export const FundingOpportunityCommitmentTable = (props) => {
  const [filteredData, setFilteredData] = useState([]);
  const [mergedFOCs, setMergedFOCs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [isViewCommitmentModalOpen, setIsViewCommitmentModalOpen] = useState(false);
  const [isAddCommitmentModalOpen, setIsAddCommitmentModalOpen] = useState(false);
  const [isSearchCommitmentModalOpen, setIsSearchCommitmentModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailsData, setDetailsData] = useState([]);
  const [commitmentToAdd, setCommitmentToAdd] = useState(null);
  const {
    columns,
    header,
    headerSize,
    pageSize,
    dataPath,
    permissions,
    commitmentIds,
    fundingOpportunityData,
    fundingOpportunityName,
  } = props;
  const REGIONS = ['HQ', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

  const [fundingOpportunityCommitments, setFundingOpportunityCommitments] = useState(
    _.get(props, dataPath, [])
  );
  const [
    fundingOpportunityCommitmentNumbers,
    setFundingOpportunityCommitmentNumbers,
  ] = useState(_.get(props, commitmentIds, []));

  //Checking to see if the User is SMA or SMU to be able to add commitments
  const perms = _.get(props, permissions, []);
  const isUserSmaOrSmu = isSmaOrSmu(perms);

  useEffect(() => {
    const loadCommitments = async () => {
      const commitments = await mergeFOCWithCommitments();
      mergeCommitments(commitments);
    };
    loadCommitments();
  }, [fundingOpportunityCommitments]);

  const closeModalWithNewFoc = (foc) => {
    closeModal();

    setFundingOpportunityCommitmentNumbers(
      _.concat(fundingOpportunityCommitmentNumbers, foc.commitmentNumber)
    );
    setFundingOpportunityCommitments(_.concat(fundingOpportunityCommitments, foc));
  };

  const mergeFOCWithCommitments = () => {
    setLoading(true);

    return REST.commitment
      .getListCommitmentDetails(fundingOpportunityCommitmentNumbers)
      .then(REST.handler.handleResponse)
      .then((data) => {
        const commitmentsToMerge = _.get(data, '[0].commitmentDetailsResponse', []);
        return commitmentsToMerge;
      })
      .catch((e) => {
        console.error('Error retrieving additonal commitment details to merge');
        return Promise.reject(e);
      });
  };

  const mergeCommitments = (commitmentsToMerge) => {
    const mappedCommitmentsObject = new Map();

    _.forEach(commitmentsToMerge, (commitmentToMerge) => {
      mappedCommitmentsObject.set(
        commitmentToMerge.commitmentNumberId,
        commitmentToMerge
      );
    });

    const mergedData = _.map(fundingOpportunityCommitments, (item) => {
      const matchingCommitment = mappedCommitmentsObject.get(item.commitmentNumber);
      if (matchingCommitment) {
        return {
          ...item,
          ...matchingCommitment,
        };
      }
      return item;
    });

    formatCommitments(mergedData);
  };

  const formatCommitments = (commitments) => {
    let commitmentsFormatted = [];

    _.forEach(commitments, (commitmentToFormat) => {
      let newCommitment = {
        ...commitmentToFormat,
        commitmentNumberIdAndSeq: `${
          commitmentToFormat.commitmentNumberId
        }-${formatLineNumber(commitmentToFormat.commitmentLineNumber)}`,
        commitmentRegion: REGIONS[commitmentToFormat.commitmentRegion],
        commitmentAvailableAmount: showDollarAmountWithDollar(
          commitmentToFormat.commitmentAvailableAmount
        ),
        commitmentLineNumber: `${formatLineNumber(
          commitmentToFormat.commitmentLineNumber
        )}`,
        commitmentAmount: showDollarAmountWithDollar(commitmentToFormat.commitmentAmount),
        commitmentStartDate: formatDateMMDDYYYY(
          new Date(commitmentToFormat.commitmentStartDate).toISOString()
        ),
        commitmentEndDate: formatDateMMDDYYYY(
          new Date(commitmentToFormat.commitmentEndDate).toISOString()
        ),
      };
      commitmentsFormatted.push(newCommitment);
    });
    const commitmentsSortedAndFormatted = _.sortBy(commitmentsFormatted, (item) => {
      return item.commitmentNumberIdAndSeq;
    });
    setFilteredData(commitmentsSortedAndFormatted);
    setMergedFOCs(commitmentsSortedAndFormatted);
    setLoading(false);

    return commitmentsSortedAndFormatted;
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);

    const filtered = _.filter(mergedFOCs, (item) =>
      _.some(item, (value) =>
        _.includes(_.toLower(_.toString(value)), _.toLower(searchTerm))
      )
    );

    setFilteredData(filtered);
    setActivePage(1);
  };

  const handlePageChange = (e, {activePage}) => {
    setActivePage(activePage);
  };

  const openViewCommitmentModel = (detailsData) => {
    setDetailsData(detailsData);
    setIsViewCommitmentModalOpen(true);
  };

  const openSearchCommitmentModal = () => {
    setIsSearchCommitmentModalOpen(true);
  };

  const closeModal = () => {
    setIsViewCommitmentModalOpen(false);
    setIsAddCommitmentModalOpen(false);
  };

  const renderTable = () => {
    const keys = _.keys(columns);
    const startIndex = (activePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = _.slice(filteredData, startIndex, endIndex);
    return (
      <FemaGoTable columns={keys.length} withForm={false} summary={header}>
        <FemaGoTable.Header>
          <FemaGoTable.Row>
            {_.map(keys, (k) => (
              <FemaGoTable.HeaderCell key={k}>{k}</FemaGoTable.HeaderCell>
            ))}
          </FemaGoTable.Row>
        </FemaGoTable.Header>
        <FemaGoTable.Body>
          {_.map(paginatedData, (row, i) => {
            return (
              <FemaGoTable.Row key={i}>
                {_.map(keys, (k, j) => {
                  const curr = columns[k];
                  if (k === 'Actions') {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        <a href="#" onClick={() => openViewCommitmentModel(row)}>
                          {curr}
                        </a>
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'Commitment number - sequence number') {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        <a
                          href="#"
                          onClick={() => {
                            openViewCommitmentModel(row);
                          }}
                        >
                          {row.commitmentNumberIdAndSeq}
                        </a>
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'Available amount') {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        {row.commitmentAvailableAmount}
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'Region') {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        {row.commitmentRegion}
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'Fiscal year') {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        {row.commitmentBudgetFiscalYear}
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'ACCS code') {
                    return (
                      <FemaGoTable.TextCell key={j}>{row.accsline}</FemaGoTable.TextCell>
                    );
                  }
                  return (
                    <FemaGoTable.TextCell key={j}>
                      {compileContent(curr, {row}, true)}
                    </FemaGoTable.TextCell>
                  );
                })}
              </FemaGoTable.Row>
            );
          })}
        </FemaGoTable.Body>
      </FemaGoTable>
    );
  };

  return (
    <FundingOpportunityContext.Provider value={props.context.fundingOpportunityData}>
      <Segment>
        <Header content={header} as={headerSize || 'h2'} />
        <div className="search-bar commitment-search-bar">
          <Input
            icon={<Icon name="search" />}
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
          />
          {isUserSmaOrSmu && (
            <Button primary onClick={openSearchCommitmentModal}>
              Add Commitment
            </Button>
          )}
        </div>
        {loading ? (
          <div
            className="margin-top"
            style={{minHeight: '63px', display: 'flex', alignItems: 'center'}}
          >
            <Loader inline="centered" size="large" active>
              Loading...
            </Loader>
          </div>
        ) : (
          <>
            {!_.isEmpty(mergedFOCs) && renderTable()}
            {filteredData.length > pageSize && (
              <Pagination
                activePage={activePage}
                totalPages={Math.ceil(filteredData.length / pageSize)}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
        <FundingOpportunityCommitmentModal
          isOpen={isViewCommitmentModalOpen}
          isEditable={false}
          detailsData={detailsData}
          closeModal={closeModal}
        />
        <FOCommitmentSearchModal
          isOpen={isSearchCommitmentModalOpen}
          closeModal={() => setIsSearchCommitmentModalOpen(false)}
          existingCommitmentIds={fundingOpportunityCommitmentNumbers}
          setCommitmentToAdd={setCommitmentToAdd}
          setIsAddCommitmentModalOpen={setIsAddCommitmentModalOpen}
        />

        <AddFOCContainerModal
          isOpen={isAddCommitmentModalOpen}
          closeModal={closeModal}
          selectedCommitment={commitmentToAdd}
          closeModalWithNewFoc={closeModalWithNewFoc}
        />
      </Segment>
    </FundingOpportunityContext.Provider>
  );
};

export default connectDynamic(FundingOpportunityCommitmentTable);
